import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Wand2, Loader2, Save, FileDown, Menu, ArrowLeft, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useJobContext } from "@/contexts/JobContext";

interface Letter {
  tone: string;
  text: string;
}

export default function Letters() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedJob, clearSelectedJob } = useJobContext();
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [jobText, setJobText] = useState("");
  const [parsedJob, setParsedJob] = useState<any>(null);
  const [currentLetter, setCurrentLetter] = useState<Letter | null>(null);
  const [selectedTone, setSelectedTone] = useState<'professional' | 'energetic' | 'concise'>('professional');
  const [credits, setCredits] = useState(0);

  // Auto-populate from JobContext
  useEffect(() => {
    if (selectedJob) {
      const fullJobText = `${selectedJob.title} at ${selectedJob.company}\n${selectedJob.location || ''}\n\n${selectedJob.description || ''}`;
      setJobText(fullJobText);
      toast({
        title: "Job loaded!",
        description: `Creating cover letter for ${selectedJob.title} at ${selectedJob.company}`,
      });
    }
  }, [selectedJob]);

  useEffect(() => {
    checkAuth();
    loadCredits();
    loadScannedJob();
    
    // Listen for job data updates from floating panel
    const handleJobDataUpdate = (e: CustomEvent) => {
      const jobData = e.detail;
      const fullJobText = `${jobData.title} at ${jobData.company}\n${jobData.location}\n\n${jobData.description}`;
      setJobText(fullJobText);
      toast({
        title: "Job loaded!",
        description: `${jobData.title} at ${jobData.company}`,
      });
    };
    
    window.addEventListener('jobDataUpdated', handleJobDataUpdate as EventListener);
    
    return () => {
      window.removeEventListener('jobDataUpdated', handleJobDataUpdate as EventListener);
    };
  }, []);

  const loadScannedJob = () => {
    const scannedJobStr = sessionStorage.getItem('scannedJob');
    if (scannedJobStr) {
      try {
        const scannedJob = JSON.parse(scannedJobStr);
        const fullJobText = `${scannedJob.title} at ${scannedJob.company}\n${scannedJob.location}\n\n${scannedJob.description}`;
        setJobText(fullJobText);
        sessionStorage.removeItem('scannedJob'); // Clear after use
        toast({
          title: "Job loaded!",
          description: `${scannedJob.title} at ${scannedJob.company}`,
        });
      } catch (error) {
        console.error('Failed to load scanned job:', error);
      }
    }
  };

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
    }
  };

  const loadCredits = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data } = await supabase
      .from('credits')
      .select('total_credits')
      .eq('user_id', session.user.id)
      .single();

    if (data) setCredits(data.total_credits);
  };

  const parseJob = async () => {
    if (!jobText || jobText.trim().length < 50) {
      toast({
        variant: "destructive",
        title: "Input required",
        description: "Please paste a complete job description (at least 50 characters).",
      });
      return;
    }

    setParsing(true);
    try {
      const { data, error } = await supabase.functions.invoke('parse-job', {
        body: { text: jobText }
      });

      if (error) throw error;

      setParsedJob(data);
      toast({
        title: "Job parsed successfully",
        description: `Found: ${data.jobTitle} at ${data.company}`,
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Parsing failed",
        description: error.message || "Failed to parse job description.",
      });
    } finally {
      setParsing(false);
    }
  };

  const generateLetter = async (tone: 'professional' | 'energetic' | 'concise') => {
    if (!parsedJob) {
      toast({
        variant: "destructive",
        title: "Parse job first",
        description: "Please parse a job posting before generating a letter.",
      });
      return;
    }

    setGenerating(true);
    setSelectedTone(tone);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Get top achievements from work history
      const { data: workData } = await supabase
        .from('work_experiences')
        .select('achievements')
        .eq('user_id', session.user.id)
        .limit(3);

      const achievements = workData
        ?.flatMap((w: any) => Array.isArray(w.achievements) ? w.achievements : [])
        .slice(0, 3) || [];

      // Generate job hash for idempotency
      const jobContext = `${parsedJob.jobTitle}:${parsedJob.company}`;
      const encoder = new TextEncoder();
      const data = encoder.encode(jobContext);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const jobHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      
      // Map tone to feature key
      const featureKeyMap = {
        'professional': 'cover.professional' as const,
        'energetic': 'cover.energetic' as const,
        'concise': 'cover.concise' as const,
      };
      const featureKey = featureKeyMap[tone];
      const refId = `cover:${tone}:${jobHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey, refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast({
            variant: "destructive",
            title: "Insufficient credits",
            description: errorData.message || 'Not enough credits to generate cover letter',
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Execute AI generation
      const { data: letterData, error } = await supabase.functions.invoke('ai-generate-letters', {
        body: {
          jobTitle: parsedJob.jobTitle,
          company: parsedJob.company,
          jobRequirements: parsedJob.requirements,
          achievements,
          tone
        }
      });

      if (error) throw error;

      setCurrentLetter({ tone: letterData.tone, text: letterData.text });

      // Show success with credit info
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast({
          title: "Cover letter generated!",
          description: `Used ${creditResult.cost} credit. ${creditResult.remaining} remaining.`,
        });
      } else {
        toast({
          title: "Cover letter generated!",
          description: `Preview your ${tone} cover letter below.`,
        });
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error('[COVER-LETTER] Generation error:', error);
      toast({
        variant: "destructive",
        title: "Generation failed",
        description: error.message || "Failed to generate cover letter.",
      });
    } finally {
      setGenerating(false);
    }
  };

  const saveLetter = async () => {
    if (!currentLetter || !parsedJob) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('save-letter', {
        body: {
          jobTitle: parsedJob.jobTitle,
          company: parsedJob.company,
          letterText: currentLetter.text,
          tone: currentLetter.tone
        }
      });

      if (error) throw error;

      loadCredits(); // Refresh credits

      toast({
        title: "Letter saved!",
        description: "Your cover letter has been saved to Documents.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Save failed",
        description: error.message || "Failed to save letter.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Cover Letter Generator</h1>
          <p className="text-muted-foreground mt-2">Generate tailored cover letters for any job</p>
        </div>
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/app')} variant="outline" className="hidden md:flex">
              Back to Dashboard
            </Button>
            
            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <div className="flex flex-col gap-4 mt-8">
                  <Button variant="ghost" className="w-full justify-start" onClick={() => navigate('/app')}>
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Panel */}
          <Card className="p-6 space-y-4">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Paste Job Description</h2>
                {selectedJob && (
                  <Button variant="ghost" size="sm" onClick={clearSelectedJob}>
                    <X className="h-4 w-4 mr-1" />
                    Clear Job
                  </Button>
                )}
              </div>
              
              {selectedJob && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-4">
                  <p className="text-sm">
                    <strong>{selectedJob.title}</strong> at <strong>{selectedJob.company}</strong>
                  </p>
                  {selectedJob.location && (
                    <p className="text-xs text-muted-foreground mt-1">{selectedJob.location}</p>
                  )}
                </div>
              )}
              <p className="text-sm text-muted-foreground mb-4">
                Copy the complete job description from LinkedIn, Indeed, company career pages, or any job site
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="jobText">Full Job Description</Label>
                  <Textarea
                    id="jobText"
                    placeholder="Paste the complete job description here, including:&#10;• Job title&#10;• Company name&#10;• Requirements & qualifications&#10;• Responsibilities&#10;• Skills needed"
                    value={jobText}
                    onChange={(e) => setJobText(e.target.value)}
                    rows={12}
                    className="mt-2 font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    💡 Tip: Include as much detail as possible for better cover letters
                  </p>
                </div>

                <Button 
                  onClick={parseJob} 
                  disabled={parsing || !jobText || jobText.trim().length < 50}
                  className="w-full"
                >
                  {parsing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing Job Description...
                    </>
                  ) : (
                    'Analyze Job Description'
                  )}
                </Button>
              </div>

              {parsedJob && (
                <div className="mt-6 p-4 bg-muted rounded-lg space-y-3">
                  <h3 className="font-semibold">✓ Job Analysis Complete</h3>
                  <div className="space-y-2">
                    <p className="text-sm"><strong>Title:</strong> {parsedJob.jobTitle}</p>
                    <p className="text-sm"><strong>Company:</strong> {parsedJob.company}</p>
                    {parsedJob.location && (
                      <p className="text-sm"><strong>Location:</strong> {parsedJob.location}</p>
                    )}
                    {parsedJob.requirements && parsedJob.requirements.length > 0 && (
                      <div className="text-sm">
                        <strong>Key Requirements ({parsedJob.requirements.length}):</strong>
                        <ul className="list-disc list-inside ml-2 mt-1">
                          {parsedJob.requirements.slice(0, 3).map((req: string, i: number) => (
                            <li key={i} className="text-muted-foreground">{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {parsedJob && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm text-muted-foreground">Choose a tone to generate:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Button 
                      onClick={() => generateLetter('professional')} 
                      disabled={generating}
                      variant={selectedTone === 'professional' ? 'default' : 'outline'}
                      className="w-full"
                    >
                      {generating && selectedTone === 'professional' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Professional'
                      )}
                    </Button>
                    <Button 
                      onClick={() => generateLetter('energetic')} 
                      disabled={generating}
                      variant={selectedTone === 'energetic' ? 'default' : 'outline'}
                      className="w-full"
                    >
                      {generating && selectedTone === 'energetic' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Energetic'
                      )}
                    </Button>
                    <Button 
                      onClick={() => generateLetter('concise')} 
                      disabled={generating}
                      variant={selectedTone === 'concise' ? 'default' : 'outline'}
                      className="w-full"
                    >
                      {generating && selectedTone === 'concise' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Concise'
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Preview Panel */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Cover Letter Preview</h2>

            {currentLetter ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge variant="secondary" className="capitalize">
                    {currentLetter.tone} Tone
                  </Badge>
                  <p className="text-sm text-muted-foreground">
                    Edit freely • Credit used only when saved
                  </p>
                </div>

                <Textarea
                  value={currentLetter.text}
                  onChange={(e) => {
                    setCurrentLetter({ ...currentLetter, text: e.target.value });
                  }}
                  onCopy={(e) => e.preventDefault()}
                  onCut={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  rows={16}
                  className="font-serif select-none"
                />

                <div className="flex gap-2">
                  <Button 
                    onClick={saveLetter} 
                    disabled={loading || credits < 1}
                    className="flex-1"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save to Documents (1 credit)
                      </>
                    )}
                  </Button>
                  <Button variant="outline" disabled className="flex-1">
                    <FileDown className="mr-2 h-4 w-4" />
                    Export PDF
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  Try different tones for free • Credit charged only when you save
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[500px] text-muted-foreground">
                <div className="text-center space-y-2">
                  <p>Generate a cover letter to preview it here</p>
                  <p className="text-xs">Credits only used when you save</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
